import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  FormControl,
  FormGroup,
  Modal,
  Pagination,
  Table,
} from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import NotificationSystem from "react-notification-system";

import {
  blockCashlessOpdContact,
  createOutreachCancelSource,
  fetchCashlessOpdContactEligibility,
  fetchCashlessOpdOutreachDashboard,
  isOutreachRequestCancelled,
  unblockCashlessOpdContact,
} from "api/cashlessOpdOutreach/cashlessOpdOutreach";
import { getApiErrorMessage } from "utils/getApiErrorMessage";
import { style } from "variables/Variables";

import "./cashlessOpdOutreach.css";

const TIMEZONE = "Asia/Colombo";
const ADMIN_ROLES = [1, 2];
const PAGE_SIZES = [25, 50, 100];
const EMPTY_SUMMARY = {
  selected: 0,
  sent: 0,
  failed: 0,
  queued: 0,
  skipped: 0,
  opted_out: 0,
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (error) {
    return null;
  }
};

const getColomboToday = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "2-digit",
    timeZone: TIMEZONE,
    year: "numeric",
  }).formatToParts(new Date());
  const values = parts.reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  return `${values.year}-${values.month}-${values.day}`;
};

const readFilters = (search) => {
  const params = new URLSearchParams(search || "");
  const status = params.get("status") || "";
  const optedOut = params.get("opted_out") || "";
  const pageSize = Number(params.get("records_per_page"));
  return {
    date: /^\d{4}-\d{2}-\d{2}$/.test(params.get("date") || "")
      ? params.get("date")
      : getColomboToday(),
    optedOut: optedOut === "true" || optedOut === "false" ? optedOut : "",
    page: Math.max(Number(params.get("page")) || 1, 1),
    recordsPerPage: PAGE_SIZES.indexOf(pageSize) >= 0 ? pageSize : 25,
    search: params.get("search") || "",
    status: ["queued", "sending", "sent", "failed", "skipped"].indexOf(status) >= 0
      ? status
      : "",
  };
};

const serializeFilters = (filters) => {
  const params = new URLSearchParams();
  params.set("date", filters.date);
  params.set("timezone", TIMEZONE);
  if (filters.status) params.set("status", filters.status);
  if (filters.search) params.set("search", filters.search);
  if (filters.optedOut) params.set("opted_out", filters.optedOut);
  params.set("page", String(filters.page));
  params.set("records_per_page", String(filters.recordsPerPage));
  return `?${params.toString()}`;
};

const filtersEqual = (left, right) =>
  left.date === right.date &&
  left.optedOut === right.optedOut &&
  left.page === right.page &&
  left.recordsPerPage === right.recordsPerPage &&
  left.search === right.search &&
  left.status === right.status;

const unwrapPayload = (response) => {
  let payload = (response && response.data) || {};
  for (let depth = 0; depth < 3; depth += 1) {
    if (payload && payload.summary && payload.customers) return payload;
    if (payload && payload.data && !Array.isArray(payload.data)) {
      payload = payload.data;
    } else {
      break;
    }
  }
  return payload || {};
};

const normalizeDashboard = (response, requestedPage, requestedPageSize) => {
  const payload = unwrapPayload(response);
  const customers = payload.customers || {};
  const data = Array.isArray(customers.data)
    ? customers.data.filter((customer) => customer && typeof customer === "object")
    : [];
  return {
    customers: data,
    pagination: {
      currentPage: Math.max(Number(customers.current_page) || requestedPage, 1),
      lastPage: Math.max(Number(customers.last_page) || 1, 1),
      perPage: Number(customers.per_page) || requestedPageSize,
      total: Math.max(Number(customers.total) || 0, 0),
    },
    summary: { ...EMPTY_SUMMARY, ...(payload.summary || {}) },
  };
};

const getValidationError = (error, field) => {
  const data = (error && error.response && error.response.data) || {};
  const errors = data.errors || (data.data && data.data.errors) || {};
  const value = errors[field];
  if (Array.isArray(value)) return value[0] ? String(value[0]) : "";
  return value ? String(value) : "";
};

const getOutreachError = (error) => {
  const status = error && error.response ? Number(error.response.status) : 0;
  if (status === 401) return "Your session is no longer valid. Please sign in again.";
  if (status === 403) return "You do not have permission to view this outreach dashboard.";
  if (status === 409) return "This contact was changed by another request. Refresh and try again.";
  if (status === 422) return getApiErrorMessage(error) || "Please check the submitted information.";
  if (status >= 500) return "The outreach service is temporarily unavailable. Please try again.";
  const message = getApiErrorMessage(error);
  return message === "Please Check internet"
    ? "Unable to reach the outreach service. Check your connection and try again."
    : message;
};

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    timeZone: TIMEZONE,
    year: "numeric",
  }).format(date);
};

const displayValue = (value) =>
  value === undefined || value === null || value === "" ? "—" : value;

const getPageNumbers = (currentPage, lastPage) => {
  const first = Math.max(1, Math.min(currentPage - 2, lastPage - 4));
  const final = Math.min(lastPage, first + 4);
  const pages = [];
  for (let page = first; page <= final; page += 1) pages.push(page);
  return pages;
};

const SummaryCard = ({ label, value, tone }) => (
  <div className={`opd-summary-card opd-summary-card--${tone}`}>
    <span>{label}</span>
    <strong>{Number(value) || 0}</strong>
  </div>
);

const StatusBadge = ({ status }) => {
  const normalized = String(status || "unknown").toLowerCase();
  return (
    <span className={`opd-status-badge opd-status-badge--${normalized}`}>
      {status ? normalized.replace(/_/g, " ") : "—"}
    </span>
  );
};

const LoadingRows = () =>
  Array.from({ length: 6 }, (_, rowIndex) => (
    <tr key={`opd-loading-${rowIndex}`}>
      {Array.from({ length: 9 }, (__, cellIndex) => (
        <td key={`opd-loading-${rowIndex}-${cellIndex}`}>
          <Skeleton width={cellIndex === 1 ? 120 : 72} />
        </td>
      ))}
    </tr>
  ));

const AccessState = ({ message }) => (
  <div className="main-content opd-outreach-page">
    <div className="container-fluid">
      <div className="opd-access-state" role="alert">
        <i className="fa fa-lock" aria-hidden="true" />
        <h2>Cashless OPD WhatsApp Outreach</h2>
        <p>{message}</p>
      </div>
    </div>
  </div>
);

const CashlessOpdOutreach = (props) => {
  const notificationRef = useRef(null);
  const cancelSourceRef = useRef(null);
  const storedUserRef = useRef(getStoredUser());
  const storedUser = storedUserRef.current;
  const initialFiltersRef = useRef(readFilters(props.location.search));

  const [filters, setFilters] = useState(initialFiltersRef.current);
  const [searchInput, setSearchInput] = useState(initialFiltersRef.current.search);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: initialFiltersRef.current.page,
    lastPage: 1,
    perPage: initialFiltersRef.current.recordsPerPage,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [accessError, setAccessError] = useState("");
  const [runningLeadIds, setRunningLeadIds] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  const notify = useCallback((level, message) => {
    if (!notificationRef.current) return;
    notificationRef.current.addNotification({
      autoDismiss: level === "success" ? 6 : 10,
      level,
      message,
      position: "tr",
    });
  }, []);

  const loadDashboard = useCallback(
    (activeFilters) => {
      if (!storedUser || !storedUser.token || ADMIN_ROLES.indexOf(Number(storedUser.role)) < 0) {
        return Promise.resolve();
      }
      if (cancelSourceRef.current) cancelSourceRef.current.cancel("Replaced by a newer dashboard request.");
      const cancelSource = createOutreachCancelSource();
      cancelSourceRef.current = cancelSource;
      setLoading(true);
      setLoadError("");

      return fetchCashlessOpdOutreachDashboard(
        storedUser.token,
        {
          date: activeFilters.date,
          opted_out: activeFilters.optedOut,
          page: activeFilters.page,
          records_per_page: activeFilters.recordsPerPage,
          search: activeFilters.search,
          status: activeFilters.status,
          timezone: TIMEZONE,
        },
        cancelSource.token,
      )
        .then((response) => {
          const dashboard = normalizeDashboard(
            response,
            activeFilters.page,
            activeFilters.recordsPerPage,
          );
          setCustomers(dashboard.customers);
          setPagination(dashboard.pagination);
          setSummary(dashboard.summary);
          setAccessError("");
          setHasLoaded(true);
        })
        .catch((error) => {
          if (isOutreachRequestCancelled(error)) return;
          const status = error && error.response ? Number(error.response.status) : 0;
          const message = getOutreachError(error);
          if (status === 401 || status === 403) setAccessError(message);
          else setLoadError(message);
          setHasLoaded(true);
        })
        .then(() => {
          if (cancelSourceRef.current === cancelSource) setLoading(false);
        });
    },
    [storedUser],
  );

  useEffect(() => {
    if (!storedUser || !storedUser.token) {
      props.history.push("/auth/login-page");
      return;
    }
    if (ADMIN_ROLES.indexOf(Number(storedUser.role)) < 0) {
      setAccessError("This page is available only to admin and super-admin users.");
    }
  }, [props.history, storedUser]);

  useEffect(() => {
    const nextFilters = readFilters(props.location.search);
    setFilters((current) => (filtersEqual(current, nextFilters) ? current : nextFilters));
    setSearchInput(nextFilters.search);
  }, [props.location.search]);

  useEffect(() => {
    const nextSearch = serializeFilters(filters);
    if (props.location.search !== nextSearch) props.history.replace({ search: nextSearch });
    loadDashboard(filters);
    return () => {
      if (cancelSourceRef.current) cancelSourceRef.current.cancel("Dashboard request cancelled.");
    };
  }, [filters, loadDashboard, props.history, props.location.search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((current) => {
        const nextSearch = searchInput.trim();
        return current.search === nextSearch
          ? current
          : { ...current, page: 1, search: nextSearch };
      });
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value, page: 1 }));
  };

  const openConfirmation = (mode, customer) => {
    if (runningLeadIds.indexOf(String(customer.lead_id)) >= 0) return;
    setConfirmation({ customer, mode });
    setReason("");
    setReasonError("");
  };

  const closeConfirmation = () => {
    if (confirmation && runningLeadIds.indexOf(String(confirmation.customer.lead_id)) >= 0) return;
    setConfirmation(null);
    setReason("");
    setReasonError("");
  };

  const submitConfirmation = async () => {
    if (!confirmation || !storedUser || !storedUser.token) return;
    const cleanReason = reason.trim();
    if (!cleanReason) {
      setReasonError(
        confirmation.mode === "unblock"
          ? "Confirm why the customer requested WhatsApp messages again."
          : "Enter a reason for blocking WhatsApp outreach.",
      );
      return;
    }

    const leadId = confirmation.customer.lead_id;
    const leadKey = String(leadId);
    if (runningLeadIds.indexOf(leadKey) >= 0) return;
    setRunningLeadIds((current) => current.concat(leadKey));
    setReasonError("");

    try {
      if (confirmation.mode === "block") {
        await blockCashlessOpdContact(storedUser.token, leadId, cleanReason);
      } else {
        await unblockCashlessOpdContact(storedUser.token, leadId, cleanReason);
      }

      const eligibilityResponse = await fetchCashlessOpdContactEligibility(
        storedUser.token,
        leadId,
      );
      const eligibility = (eligibilityResponse && eligibilityResponse.data) || {};
      setCustomers((current) =>
        current.map((customer) =>
          String(customer.lead_id) === leadKey
            ? { ...customer, ...eligibility }
            : customer,
        ),
      );
      await loadDashboard(filters);
      notify(
        "success",
        confirmation.mode === "block"
          ? "WhatsApp outreach blocked for this customer."
          : "WhatsApp outreach restored for this customer.",
      );
      setConfirmation(null);
      setReason("");
    } catch (error) {
      const fieldError = getValidationError(error, "reason");
      setReasonError(fieldError || getOutreachError(error));
    } finally {
      setRunningLeadIds((current) => current.filter((id) => id !== leadKey));
    }
  };

  if (!storedUser || !storedUser.token) return null;
  if (accessError) return <AccessState message={accessError} />;

  const isInitialLoading = loading && !hasLoaded;
  const pageNumbers = getPageNumbers(pagination.currentPage, pagination.lastPage);
  const firstRecord = pagination.total
    ? (pagination.currentPage - 1) * pagination.perPage + 1
    : 0;
  const lastRecord = Math.min(
    pagination.currentPage * pagination.perPage,
    pagination.total,
  );
  const activeLeadKey = confirmation ? String(confirmation.customer.lead_id) : "";
  const submitting = runningLeadIds.indexOf(activeLeadKey) >= 0;

  return (
    <div className="main-content opd-outreach-page">
      <NotificationSystem ref={notificationRef} style={style} />
      <div className="container-fluid">
        <header className="opd-page-header">
          <div>
            <h1>Cashless OPD WhatsApp Outreach</h1>
            <p>Review campaign delivery and manage customer contact eligibility.</p>
          </div>
          <div className="opd-timezone-label">
            <i className="fa fa-clock-o" aria-hidden="true" /> Asia/Colombo
          </div>
        </header>

        <section className="opd-summary-grid" aria-label="Outreach summary">
          <SummaryCard label="Selected" value={summary.selected} tone="selected" />
          <SummaryCard label="Sent" value={summary.sent} tone="sent" />
          <SummaryCard label="Failed" value={summary.failed} tone="failed" />
          <SummaryCard label="Queued" value={summary.queued} tone="queued" />
          <SummaryCard label="Skipped" value={summary.skipped} tone="skipped" />
          <SummaryCard label="Opted Out" value={summary.opted_out} tone="opted-out" />
        </section>

        <section className="opd-filter-panel" aria-labelledby="outreach-filters-title">
          <div className="opd-section-heading">
            <div>
              <h2 id="outreach-filters-title">Filters</h2>
              <p>Results update automatically when a filter changes.</p>
            </div>
            {loading && hasLoaded ? (
              <span className="opd-refresh-label" role="status">
                <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Updating results…
              </span>
            ) : null}
          </div>
          <div className="opd-filter-grid">
            <FormGroup className="opd-field">
              <label htmlFor="opd-outreach-date">Date</label>
              <FormControl
                id="opd-outreach-date"
                type="date"
                value={filters.date}
                onChange={(event) => updateFilter("date", event.target.value)}
              />
            </FormGroup>
            <FormGroup className="opd-field">
              <label htmlFor="opd-outreach-status">Status</label>
              <FormControl
                componentClass="select"
                id="opd-outreach-status"
                value={filters.status}
                onChange={(event) => updateFilter("status", event.target.value)}
              >
                <option value="">All statuses</option>
                <option value="queued">Queued</option>
                <option value="sending">Sending</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
              </FormControl>
            </FormGroup>
            <FormGroup className="opd-field">
              <label htmlFor="opd-outreach-eligibility">Contact eligibility</label>
              <FormControl
                componentClass="select"
                id="opd-outreach-eligibility"
                value={filters.optedOut}
                onChange={(event) => updateFilter("optedOut", event.target.value)}
              >
                <option value="">All</option>
                <option value="false">Contactable</option>
                <option value="true">Opted Out</option>
              </FormControl>
            </FormGroup>
            <FormGroup className="opd-field opd-search-field">
              <label htmlFor="opd-outreach-search">Search</label>
              <div className="opd-search-control">
                <i className="fa fa-search" aria-hidden="true" />
                <FormControl
                  id="opd-outreach-search"
                  type="search"
                  value={searchInput}
                  placeholder="Lead ID, customer, masked mobile or organization"
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>
            </FormGroup>
            <FormGroup className="opd-field">
              <label htmlFor="opd-outreach-page-size">Rows</label>
              <FormControl
                componentClass="select"
                id="opd-outreach-page-size"
                value={filters.recordsPerPage}
                onChange={(event) => updateFilter("recordsPerPage", Number(event.target.value))}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </FormControl>
            </FormGroup>
          </div>
        </section>

        <section className="opd-table-panel" aria-labelledby="outreach-customers-title">
          <div className="opd-section-heading opd-table-heading">
            <div>
              <h2 id="outreach-customers-title">Customers</h2>
              <p>{firstRecord}–{lastRecord} of {pagination.total} records</p>
            </div>
            <span>Page {pagination.currentPage} of {pagination.lastPage}</span>
          </div>

          {loadError ? (
            <div className="opd-state-panel" role="alert">
              <i className="fa fa-exclamation-circle" aria-hidden="true" />
              <h3>Unable to load outreach records</h3>
              <p>{loadError}</p>
              <Button onClick={() => loadDashboard(filters)}>
                <i className="fa fa-refresh" aria-hidden="true" /> Retry
              </Button>
            </div>
          ) : (
            <div className={`opd-table-wrap ${loading && hasLoaded ? "is-refreshing" : ""}`}>
              <Table className="opd-customer-table">
                <thead>
                  <tr>
                    <th scope="col">Lead ID</th>
                    <th scope="col">Customer</th>
                    <th scope="col">Masked mobile</th>
                    <th scope="col">Organization</th>
                    <th scope="col">Status</th>
                    <th scope="col">Today’s sent time</th>
                    <th scope="col">First successful message</th>
                    <th scope="col">Contact eligibility</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isInitialLoading ? (
                    <LoadingRows />
                  ) : customers.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="opd-empty-cell">
                        <div className="opd-state-panel">
                          <i className="fa fa-filter" aria-hidden="true" />
                          <h3>No customers found</h3>
                          <p>No outreach records match the current filters.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    customers.map((customer) => {
                      const leadKey = String(customer.lead_id);
                      const actionRunning = runningLeadIds.indexOf(leadKey) >= 0;
                      const organization = customer.organization || {};
                      return (
                        <tr key={customer.message_log_id || leadKey}>
                          <td><strong>#{displayValue(customer.lead_id)}</strong></td>
                          <td>{displayValue(customer.name)}</td>
                          <td className="opd-masked-mobile">{displayValue(customer.mobile_no_masked)}</td>
                          <td>{displayValue(organization.company_name)}</td>
                          <td><StatusBadge status={customer.status} /></td>
                          <td>{formatTimestamp(customer.today_message_sent_at)}</td>
                          <td>{formatTimestamp(customer.first_message_sent_at)}</td>
                          <td>
                            <span className={`opd-eligibility opd-eligibility--${customer.can_contact ? "contactable" : "blocked"}`}>
                              <i className={`fa fa-${customer.can_contact ? "check-circle" : "ban"}`} aria-hidden="true" />
                              {customer.can_contact ? "Contactable" : "Opted Out"}
                            </span>
                          </td>
                          <td className="opd-actions-cell">
                            {customer.whatsapp_opted_out ? (
                              <Button
                                bsSize="small"
                                bsStyle="success"
                                disabled={actionRunning}
                                onClick={() => openConfirmation("unblock", customer)}
                              >
                                {actionRunning ? <i className="fa fa-spinner fa-spin" aria-hidden="true" /> : null}
                                {actionRunning ? " Updating…" : "Unblock"}
                              </Button>
                            ) : customer.can_contact ? (
                              <Button
                                bsSize="small"
                                bsStyle="danger"
                                disabled={actionRunning}
                                onClick={() => openConfirmation("block", customer)}
                              >
                                {actionRunning ? <i className="fa fa-spinner fa-spin" aria-hidden="true" /> : null}
                                {actionRunning ? " Updating…" : "Block WhatsApp"}
                              </Button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}

          {!loadError && !isInitialLoading && pagination.total > 0 ? (
            <div className="opd-pagination-bar">
              <span>{firstRecord}–{lastRecord} of {pagination.total}</span>
              <Pagination>
                <Pagination.First
                  disabled={pagination.currentPage <= 1 || loading}
                  onClick={() => setFilters((current) => ({ ...current, page: 1 }))}
                />
                <Pagination.Prev
                  disabled={pagination.currentPage <= 1 || loading}
                  onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}
                />
                {pageNumbers.map((page) => (
                  <Pagination.Item
                    active={page === pagination.currentPage}
                    disabled={loading}
                    key={page}
                    onClick={() => setFilters((current) => ({ ...current, page }))}
                  >
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={pagination.currentPage >= pagination.lastPage || loading}
                  onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
                />
                <Pagination.Last
                  disabled={pagination.currentPage >= pagination.lastPage || loading}
                  onClick={() => setFilters((current) => ({ ...current, page: pagination.lastPage }))}
                />
              </Pagination>
            </div>
          ) : null}
        </section>
      </div>

      <Modal
        backdrop={submitting ? "static" : true}
        className="opd-confirmation-modal"
        keyboard={!submitting}
        onHide={closeConfirmation}
        show={Boolean(confirmation)}
      >
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>
            {confirmation && confirmation.mode === "block"
              ? "Block WhatsApp outreach"
              : "Unblock WhatsApp outreach"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmation ? (
            <React.Fragment>
              <p className="opd-confirmation-customer">
                <strong>{displayValue(confirmation.customer.name)}</strong>
                <span>Lead #{displayValue(confirmation.customer.lead_id)} · {displayValue(confirmation.customer.mobile_no_masked)}</span>
              </p>
              {confirmation.mode === "block" ? (
                <Alert bsStyle="warning">
                  Future OpenClaw campaign sends to this customer will be prevented immediately.
                </Alert>
              ) : (
                <Alert bsStyle="info">
                  Only continue if the customer explicitly requested WhatsApp messages again.
                </Alert>
              )}
              <FormGroup validationState={reasonError ? "error" : null}>
                <label htmlFor="opd-confirmation-reason">
                  {confirmation.mode === "block" ? "Reason for blocking" : "Reason for unblocking"}
                </label>
                <FormControl
                  autoFocus
                  componentClass="textarea"
                  disabled={submitting}
                  id="opd-confirmation-reason"
                  maxLength={1000}
                  rows={4}
                  value={reason}
                  aria-describedby={reasonError ? "opd-reason-error" : undefined}
                  aria-invalid={Boolean(reasonError)}
                  onChange={(event) => {
                    setReason(event.target.value);
                    if (reasonError) setReasonError("");
                  }}
                />
                {reasonError ? (
                  <span className="help-block" id="opd-reason-error" role="alert">{reasonError}</span>
                ) : null}
              </FormGroup>
            </React.Fragment>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={submitting} onClick={closeConfirmation}>Cancel</Button>
          <Button
            bsStyle={confirmation && confirmation.mode === "block" ? "danger" : "success"}
            disabled={submitting}
            onClick={submitConfirmation}
          >
            {submitting ? <i className="fa fa-spinner fa-spin" aria-hidden="true" /> : null}
            {submitting
              ? " Saving…"
              : confirmation && confirmation.mode === "block"
                ? "Block WhatsApp"
                : "Confirm unblock"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CashlessOpdOutreach;
